import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { AuthService } from './auth.service';
import {
  loadStationingAlignment, loadStationingPreference, saveStationingAlignment, saveStationingPreference,
} from '../helpers/map-layer-view-state';

/**
 * 7.2: whether the project map shows Stationing, shared by the View panel's Layers toggle and the map. On by default;
 * the user's choice is remembered per account and project. Root service: keyed to the account, like every root cache.
 */
@Injectable({ providedIn: 'root' })
export class StationingPreferenceService {
  private auth = inject(AuthService);
  private readonly state = new BehaviorSubject<{ key: string; visible: boolean } | null>(null);
  private readonly lockState = new BehaviorSubject<{ key: string; name: string | null } | null>(null);

  /** The project's setting now and on every toggle */
  visible$(projectId: string): Observable<boolean> {
    return this.state.pipe(
      map(() => this.visible(projectId)),
      distinctUntilChanged(),
    );
  }

  visible(projectId: string): boolean {
    const account = this.accountId();
    const current = this.state.value;
    return current?.key === `${account}:${projectId}` ? current.visible : loadStationingPreference(account, projectId);
  }

  setVisible(projectId: string, visible: boolean): void {
    const account = this.accountId();
    saveStationingPreference(account, projectId, visible);
    this.state.next({ key: `${account}:${projectId}`, visible });
  }

  toggle(projectId: string): void {
    this.setVisible(projectId, !this.visible(projectId));
  }

  /** §3c: the locked alignment's name, or null for Automatic (the nearest), now and on every change */
  alignment$(projectId: string): Observable<string | null> {
    return this.lockState.pipe(map(() => this.alignment(projectId)), distinctUntilChanged());
  }

  alignment(projectId: string): string | null {
    const account = this.accountId();
    const current = this.lockState.value;
    return current?.key === `${account}:${projectId}` ? current.name : loadStationingAlignment(account, projectId);
  }

  setAlignment(projectId: string, name: string | null): void {
    const account = this.accountId();
    saveStationingAlignment(account, projectId, name);
    this.lockState.next({ key: `${account}:${projectId}`, name });
  }

  private accountId(): string {
    return this.auth.getCurrentUser()?.id ?? '';
  }
}
